import { createContext, useContext, useState, useEffect } from "react";
import api, { startDlTokenRefresh, stopDlTokenRefresh, setSession } from "@/lib/api";
import ActivationScreen from "@/components/ActivationScreen";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [loading, setLoading] = useState(true);
  const [activation, setActivation] = useState(null); // null=unknown, false=ok, obj=locked
  const [expiryDays, setExpiryDays] = useState(null);
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: act } = await api.get("/activation/status");
        if (act?.needs_activation) {
          setActivation(act);
          setLoading(false);
          return;
        }
        setActivation(false);
        setIsMaster(!!act?.is_master);
        setExpiryDays(typeof act?.days_left === "number" ? act.days_left : null);
      } catch {
        setActivation(false); // never hard-block on a transient error
      }
      try {
        const { data } = await api.get("/auth/me");
        setSession(true);
        await startDlTokenRefresh();
        setUser(data);
      } catch {
        setSession(false);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(true); // token zostaje wyłącznie w ciasteczku httpOnly
    await startDlTokenRefresh();
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setSession(false);
    stopDlTokenRefresh();
    setUser(false);
  };

  if (activation && activation.needs_activation) {
    return <ActivationScreen info={activation} />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, isMaster }}>
      {expiryDays != null && expiryDays <= 30 && (
        <div data-testid="license-expiry-banner"
          style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}
          className={`text-center text-xs py-1.5 px-4 ${expiryDays < 0 ? "bg-red-700 text-white" : "bg-amber-500 text-black"}`}>
          {expiryDays < 0
            ? `Licencja wygasła — skontaktuj się z dostawcą, aby odnowić.`
            : `Uwaga: licencja wygasa za ${expiryDays} dni. Skontaktuj się z dostawcą, aby ją odnowić.`}
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
